import { map } from "nanostores";
import { ApiError, type AssetInfo, getAsset, type HotbarSlot } from "../net/api";
import { $settings, $settingsLoaded, $settingsSaving, $unavailable, clearSlot, saveSettings, toggleAsset } from "./settings";
import { toolName } from "./tools";

export type ToggleResult = "added" | "removed" | "full" | "unavailable";

/** Display names of hotbar assets that are not mine (public or shared), looked up once per code. */
export const $slotNames = map<Record<string, string>>({});

/** Puts code in the first free slot, or takes it out if it is already there, and saves (spec 2g §3.2). */
export function toggleHotbar(code: string, name?: string): ToggleResult {
  if (!$settingsLoaded.get() || $settingsSaving.get()) return "unavailable";
  const { doc, result } = toggleAsset($settings.get(), code);
  if (result === "full") return result;
  if (name) $slotNames.setKey(code, name);
  void saveSettings(doc);
  return result;
}

export function toggleMessage(r: ToggleResult, name: string): string {
  if (r === "added") return `已加入快捷列：${name}`;
  if (r === "removed") return "已移出快捷列";
  if (r === "full") return "快捷列已滿";
  return "設定還沒載入或正在儲存，請稍候";
}

/** Empties the slot holding code, if any, and saves: a transferred asset leaves the hotbar. */
export function dropFromHotbar(code: string): void {
  const doc = $settings.get();
  const at = doc.hotbar.findIndex((s) => s?.kind === "asset" && s.code === code);
  if (at >= 0) void saveSettings(clearSlot(doc, at));
}

const pending = new Set<string>();

/**
 * Looks up the names of hotbar assets that are not mine. A code the server refuses (404, 403) goes to $unavailable;
 * after a network or server error it stays usable and is asked again on the next hotbar or asset change.
 */
export async function resolveSlotNames(bar: (HotbarSlot | null)[], mine: AssetInfo[], lookup: (code: string) => Promise<AssetInfo> = getAsset): Promise<void> {
  const known = new Set(mine.map((a) => a.code));
  const todo = bar.flatMap((s) =>
    s?.kind === "asset" && !known.has(s.code) && !(s.code in $slotNames.get()) && !$unavailable.get().includes(s.code) && !pending.has(s.code) ? [s.code] : []);
  for (const code of todo) pending.add(code);
  await Promise.all(todo.map(async (code) => {
    try {
      $slotNames.setKey(code, (await lookup(code)).name);
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 403)) $unavailable.set([...$unavailable.get(), code]);
    } finally {
      pending.delete(code);
    }
  }));
}

export interface SlotView { text: string; unavailable: boolean }

export function slotView(slot: HotbarSlot | null, mine: AssetInfo[], names: Record<string, string>, unavailable: string[]): SlotView {
  if (!slot) return { text: "", unavailable: false };
  if (slot.kind === "tool") return { text: toolName(slot.tool), unavailable: false };
  const own = mine.find((a) => a.code === slot.code);
  if (own) return { text: own.name, unavailable: false };
  if (unavailable.includes(slot.code)) return { text: "不可用", unavailable: true };
  return { text: names[slot.code] ?? slot.code, unavailable: false };
}

export type SlotAction = { kind: "empty" } | { kind: "place"; code: string } | { kind: "tool"; id: string } | { kind: "unavailable" };

/** What pressing the slot's number key does. */
export function slotAction(slot: HotbarSlot | null, unavailable: string[], mine: AssetInfo[]): SlotAction {
  if (!slot) return { kind: "empty" };
  if (slot.kind === "tool") return { kind: "tool", id: slot.tool };
  if (unavailable.includes(slot.code) && !mine.some((a) => a.code === slot.code)) return { kind: "unavailable" };
  return { kind: "place", code: slot.code };
}
